import { call, put, takeEvery, takeLatest } from "redux-saga/effects";

// Login Redux States
import { AuthLoginActionTypes } from "./types";
import {
  authLoginApiResponseSuccess,
  authLoginApiResponseError,
} from "./actions";

import Cookies from "js-cookie";
import { URLS } from "../../../constants";
import api from "../../../utils/api";

interface LoginResponse {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  telephone?: string;
  redirect_url?: string;
}

/**
 * Checks if the user object indicates a desktop/agent app authentication flow
 * by verifying the presence of redirect_uri, state, and code_challenge.
 */
function isDesktopAppAuth(user: any): boolean {
  return !!(user?.redirect_uri && user?.state && user?.code_challenge);
}

/**
 * Validates that a redirect URL is safe to use in an iframe.
 * Only allows URLs that match the expected redirect_uri origin or localhost.
 * 
 * @param redirectUrl - The URL to validate
 * @param expectedRedirectUri - The expected redirect URI from the user request
 * @returns true if the URL is safe, false otherwise
 */
function isValidRedirectUrl(redirectUrl: string, expectedRedirectUri?: string): boolean {
  try {
    const url = new URL(redirectUrl);
    
    // Allow localhost URLs (common for desktop OAuth flows)
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      return true;
    }
    
    // If expectedRedirectUri is provided, validate that the origin matches
    if (expectedRedirectUri) {
      try {
        const expectedUrl = new URL(expectedRedirectUri);
        // Compare origin (protocol + hostname + port)
        if (url.origin === expectedUrl.origin) {
          return true;
        }
      } catch {
        // If expectedRedirectUri is invalid, fall through to return false
      }
    }
    
    // Reject all other URLs for security
    return false;
  } catch {
    // Invalid URL format
    return false;
  }
}

function* loginUser({ payload: { user } }: any): Generator<any, void, any> {
  try {
    const response: Response = yield call(
      fetch,
      `${URLS.CLIENT_URL}/api/login`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
        credentials: "include",
      },
    );
    const data: LoginResponse = yield response.json();

    if (!response.ok) {
      if (response.status === 404 || response.status === 401) {
        throw new Error("authNotFound");
      }
      throw new Error("internalServerError");
    }

    // Check if this is a desktop app authentication flow
    const isDesktopApp = isDesktopAppAuth(user);

    if (isDesktopApp && data.redirect_url) {
      // Validate the redirect URL before using it
      if (!isValidRedirectUrl(data.redirect_url, user.redirect_uri)) {
        throw new Error("Invalid redirect URL");
      }
      
      // Desktop app flow: send auth code to desktop app silently using an iframe
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = data.redirect_url;
      document.body.appendChild(iframe);

      // Dispatch success action to show the success message on the same page
      yield put(
        authLoginApiResponseSuccess(AuthLoginActionTypes.LOGIN_USER, { desktopAuthSuccess: true }),
      );
      return;
    }

    // Normal web login flow: store user data and continue
    localStorage.setItem("authUser", JSON.stringify(data));

    yield put(
      authLoginApiResponseSuccess(AuthLoginActionTypes.LOGIN_USER, data),
    );
  } catch (error: any) {
    // Check if this is an agent authentication scenario
    const isAgentAuth = isDesktopAppAuth(user);
    // treat it as success since the auth likely succeeded on the server
    if (
      isAgentAuth &&
      (error.message === "Failed to fetch" ||
        error.message === "Network request failed")
    ) {
      // The authentication was likely successful, but the agent expects a different redirect
      yield put(
        authLoginApiResponseError(
          AuthLoginActionTypes.LOGIN_USER,
          "agent_auth_success",
        ),
      );
    } else {
      yield put(
        authLoginApiResponseError(
          AuthLoginActionTypes.LOGIN_USER,
          error.message,
        ),
      );
    }
  }
}

function* socialLogin({ payload: { data, type } }: any) {}

function* logoutUser() {
  try {
    yield call(api.post, "/api/logout", null, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      yield put(
        authLoginApiResponseSuccess(AuthLoginActionTypes.LOGOUT_USER, true),
      );
    } else {
      yield put(
        authLoginApiResponseSuccess(AuthLoginActionTypes.LOGOUT_USER, true),
      );
    }
  } catch (error: any) {
    yield put(
      authLoginApiResponseError(AuthLoginActionTypes.LOGOUT_USER, error),
    );
  } finally {
    localStorage.removeItem("authUser");

    Cookies.remove("api-key");
    Cookies.remove("access_token");
    Cookies.remove("refresh_token");
  }
}

function* loginSaga() {
  yield takeEvery(AuthLoginActionTypes.LOGIN_USER, loginUser);
  yield takeEvery(AuthLoginActionTypes.LOGOUT_USER, logoutUser);
  yield takeLatest(AuthLoginActionTypes.SOCIAL_LOGIN, socialLogin);
}

export default loginSaga;
